import {Sale, SaleItem, SalesStatistics, SaleStatus, PaymentMethod, PaymentStatus} from '../models/sale.model';
import {
  SaleDTO,
  SaleItemDTO,
  UpdateSaleRequestDTO,
  RecordPaymentRequestDTO
} from '../models/sale.dto';

// Helper function to safely parse string numbers
const parseNumber = (value: string | undefined): number => {
  if (!value) {
    return 0;
  }
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

// Helper function to safely parse string to number
const parseString = (value: number | undefined): string => {
  return value?.toString() || '0';
};

// Map SaleItemDTO to SaleItem
export const mapSaleItemDTOToModel = (dto: SaleItemDTO): SaleItem => {
  return {
    id: dto.id.toString(),
    salesOrderId: dto.id.toString(), // Use id as salesOrderId since sales_order_id doesn't exist
    productItemId: undefined, // product_item_id doesn't exist in DTO
    productId: dto.product_id.toString(),
    productName: dto.product_name,
    quantity: parseNumber(dto.quantity),
    unitPrice: parseNumber(dto.unit_price),
    subtotal: parseNumber(dto.total_price), // Use total_price as subtotal
    unit: 'piece', // Default unit since it doesn't exist in DTO
    costPrice: undefined, // cost_price doesn't exist in DTO
    price: parseNumber(dto.unit_price), // Use unit_price as price
    serialNumber: dto.serial_number ?? null
  };
};

// Map SaleItem to SaleItemDTO
export const mapSaleItemModelToDTO = (model: SaleItem): SaleItemDTO => {
  return {
    id: model.id,
    product_id: model.productId,
    product_name: model.productName,
    product_code: model.productId, // Use productId as product_code
    quantity: parseString(model.quantity),
    unit_price: parseString(model.unitPrice),
    total_price: parseString(model.subtotal), // Use subtotal as total_price
    discount: undefined // Discount not available in SaleItem model
  };
};

// Map SaleDTO to Sale
export const mapSaleDTOToModel = (dto: SaleDTO): Sale => {
  return {
    id: dto.id,
    marketerId: dto.customer_id, // Map customer_id to marketerId
    marketerName: dto.customer_name, // Map customer_name to marketerName
    marketerPhone: dto.customer_phone, // Map customer_phone to marketerPhone
    storeId: dto.store_id,
    storeName: dto.store_name,
    totalAmount: parseNumber(dto.total_amount),
    paymentMethod: dto.payment_method as PaymentMethod,
    paymentStatus: 'overdue' as PaymentStatus, // Default payment status
    cashPaid: parseNumber(dto.paid_amount), // Map paid_amount to cashPaid
    remainingDebt: parseNumber(dto.remaining_amount), // Map remaining_amount to remainingDebt
    status: dto.status as SaleStatus,
    saleDate: dto.sale_date,
    dueDate: dto.sale_date, // Use sale_date as dueDate for now
    invoiceNumber: `INV-${dto.id}`, // Generate invoice number
    paymentTerms: '30 days', // Default payment terms
    collectionStage: 'pending', // Default collection stage
    daysOverdue: 0, // Default days overdue
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    items: dto.items.map(mapSaleItemDTOToModel),
    notes: dto.notes,
    discount: dto.discount ? parseNumber(dto.discount) : undefined,
    tax: dto.tax ? parseNumber(dto.tax) : undefined
  };
};

// Map Sale to SaleDTO
export const mapSaleModelToDTO = (model: Sale): SaleDTO => {
  return {
    id: model.id,
    customer_id: model.marketerId, // Map marketerId to customer_id
    customer_name: model.marketerName, // Map marketerName to customer_name
    customer_phone: model.marketerPhone, // Map marketerPhone to customer_phone
    store_id: model.storeId,
    store_name: model.storeName,
    total_amount: parseString(model.totalAmount),
    paid_amount: parseString(model.cashPaid), // Map cashPaid to paid_amount
    remaining_amount: parseString(model.remainingDebt), // Map remainingDebt to remaining_amount
    status: model.status,
    payment_method: model.paymentMethod,
    sale_date: model.saleDate,
    created_at: model.createdAt,
    updated_at: model.updatedAt,
    items: model.items.map(mapSaleItemModelToDTO),
    notes: model.notes,
    discount: undefined, // Discount not available in SaleItem model,
    tax: model.tax ? parseString(model.tax) : undefined
  };
};

// Map array of SaleDTOs to Sale models
export const mapSalesDTOToModel = (dtos: SaleDTO[]): Sale[] => {
  return dtos.map(mapSaleDTOToModel);
};

// Map array of Sale models to SaleDTOs
export const mapSalesModelToDTO = (models: Sale[]): SaleDTO[] => {
  return models.map(mapSaleModelToDTO);
};

// Map SalesStatisticsDTO to SalesStatistics
export const mapSalesStatisticsDTOToModel = (dto: any): SalesStatistics => {
  return {
    total_invoices: dto.total_invoices || 0,
    paid_invoices: dto.paid_invoices || 0,
    partially_paid_invoices: dto.partially_paid_invoices || 0,
    unpaid_invoices: dto.unpaid_invoices || 0,
    total_amount: dto.total_amount || 0,
    total_paid: dto.total_paid || 0,
    total_remaining: dto.total_remaining || 0,
    total_outstanding: dto.total_outstanding || 0,
    invoice_count: dto.invoice_count || 0,
    top_customers: dto.top_customers || [],
    aging_report: dto.aging_report || {
      current: 0,
      days_1_30: 0,
      days_31_60: 0,
      days_61_90: 0,
      over_90: 0
    }
  };
};


// Map Sale model to UpdateSaleRequestDTO
export const mapSaleModelToUpdateRequestDTO = (model: Partial<Sale>): UpdateSaleRequestDTO => {
  return {
    customer_id: model.marketerId, // Map marketerId to customer_id
    store_id: model.storeId,
    items: model.items?.map(item => ({
      id: item.id,
      product_id: item.productId,
      quantity: parseString(item.quantity),
      unit_price: parseString(item.unitPrice),
      discount: undefined // Discount not available in SaleItem model
    })),
    payment_method: model.paymentMethod,
    notes: model.notes,
    discount: undefined, // Discount not available in SaleItem model,
    tax: model.tax ? parseString(model.tax) : undefined,
    status: model.status
  };
};

// Create RecordPaymentRequestDTO
export const createRecordPaymentRequestDTO = (
  saleId: string,
  amount: number,
  paymentMethod: PaymentMethod,
  notes?: string,
  paymentDate?: string
): RecordPaymentRequestDTO => {
  return {
    sale_id: saleId,
    amount: parseString(amount),
    payment_method: paymentMethod,
    notes,
    payment_date: paymentDate
  };
};

// Map Legacy API SaleDTO to Domain Sale
export const mapAPISaleDTOToModel = (dto: import('../api/SalesAPIDataSource').LegacySaleDTO): Sale => {
  return {
    id: dto.id.toString(),
    marketerId: dto.marketer?.id?.toString() || '',
    marketerName: dto.marketer?.full_name || 'مسوق غير محدد',
    marketerEmail: dto.marketer?.email,
    marketerPhone: dto.marketer?.phone_number,
    marketerUsername: dto.marketer?.username,
    storeId: dto.store?.id ? dto.store.id.toString() : '',
    storeName: dto.store?.store_name || 'متجر غير محدد',
    storeManagerName: dto.store?.manager_name,
    storePhone: dto.store?.phone_number,
    storeCity: dto.store?.city,
    storeRegion: dto.store?.region,
    totalAmount: typeof dto.total_amount === 'number' ? dto.total_amount : parseNumber(String(dto.total_amount || '0')),
    paymentMethod: mapPaymentMethodToModel(dto.payment_method),
    paymentStatus: mapPaymentStatusToModel('pending'), // Default to pending since payment_status doesn't exist in new API
    cashPaid: 0, // Default to 0 since cash_paid doesn't exist in new API
    remainingDebt: 0, // Default to 0 since remaining_debt doesn't exist in new API
    status: mapStatusToModel(dto.status),
    saleDate: dto.sale_date,
    dueDate: dto.due_date,
    notes: dto.notes || undefined,
    invoiceNumber: dto.invoice_number,
    invoicePdfUrl: dto.invoice_pdf_url || undefined,
    transferImageUrl: undefined, // Not in new response
    paymentTerms: dto.payment_terms,
    collectionStage: dto.collection_stage,
    daysOverdue: dto.days_overdue,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    items: dto.items?.map(mapSaleItemAPIToModel) || [],
    discount: undefined,
    tax: undefined
  };
};

// Map Billing Invoice DTO to Domain Sale
export const mapBillingInvoiceDTOToModel = (dto: import('../api/SalesAPIDataSource').SaleDTO): Sale => {
  return {
    id: dto.id.toString(),
    marketerId: '', // Not available in billing response
    marketerName: dto.marketer_name || 'غير محدد',
    marketerEmail: undefined,
    marketerPhone: undefined,
    marketerUsername: undefined,
    storeId: '', // Not available in billing response
    storeName: dto.customer_name,
    storeManagerName: undefined,
    storePhone: undefined,
    storeCity: undefined,
    storeRegion: undefined,
    totalAmount: dto.total_amount,
    paymentMethod: mapBillingPaymentMethodToModel(dto.payment_method),
    paymentStatus: mapBillingPaymentStatusToModel(dto.payment_status),
    cashPaid: dto.paid_amount,
    remainingDebt: dto.remaining_amount,
    status: mapBillingStatusToModel(dto.status),
    saleDate: dto.issue_date,
    dueDate: dto.issue_date, // Use issue_date as due_date for now
    notes: dto.notes || undefined,
    invoiceNumber: dto.invoice_number,
    invoicePdfUrl: undefined,
    transferImageUrl: undefined,
    paymentTerms: '30 days', // Default
    collectionStage: 'pending', // Default
    daysOverdue: 0, // Default
    createdAt: dto.issue_date,
    updatedAt: dto.issue_date,
    items: [], // Not available in billing response
    discount: undefined,
    tax: undefined
  };
};

// Map Detailed Invoice DTO to Domain Sale
export const mapDetailedInvoiceDTOToModel = (dto: import('../api/SalesAPIDataSource').DetailedInvoiceDTO): Sale => {
  return {
    id: dto.id.toString(),
    marketerId: dto.marketer?.id?.toString() || '',
    marketerName: dto.marketer?.full_name || 'غير محدد',
    marketerEmail: undefined,
    marketerPhone: undefined,
    marketerUsername: undefined,
    storeId: dto.store?.id?.toString() || '',
    storeName: dto.store?.store_name || 'متجر غير محدد',
    storeManagerName: undefined,
    storePhone: dto.store?.phone_number,
    storeCity: dto.store?.city,
    storeRegion: dto.store?.region,
    totalAmount: dto.total_amount,
    paymentMethod: mapPaymentMethodToModel(dto.payment_method),
    paymentStatus: mapPaymentStatusToModel(dto.payment_status),
    cashPaid: dto.cash_paid,
    remainingDebt: dto.remaining_debt,
    status: mapStatusToModel(dto.status),
    saleDate: dto.sale_date,
    dueDate: dto.due_date,
    notes: dto.notes || undefined,
    invoiceNumber: dto.invoice_number,
    invoicePdfUrl: dto.invoice_pdf_url || undefined,
    transferImageUrl: undefined,
    paymentTerms: dto.payment_terms,
    collectionStage: dto.collection_stage,
    daysOverdue: dto.days_overdue,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    items: dto.items?.map(mapSaleItemAPIToModel) || [],
    discount: undefined,
    tax: undefined
  };
};

// Helper function to map status
const mapStatusToModel = (status: string): SaleStatus => {
  switch (status) {
    case 'delivered':
      return 'delivered';
    case 'pending':
      return 'pending';
    case 'cancelled':
      return 'cancelled';
    case 'overdue':
      return 'pending'; // Map overdue to pending for now
    default:
      return 'pending';
  }
};

// Helper function to map payment method
const mapPaymentMethodToModel = (paymentMethod: string): PaymentMethod => {
  switch (paymentMethod) {
    case 'cash':
      return 'cash';
    case 'credit':
      return 'credit';
    case 'bank_transfer':
      return 'bank_transfer';
    case 'partial':
      return 'credit'; // Map partial to credit for now
    default:
      return 'credit';
  }
};

// Helper function to map payment status
const mapPaymentStatusToModel = (paymentStatus: string): PaymentStatus => {
  switch (paymentStatus) {
    case 'paid':
      return 'paid';
    case 'pending':
      return 'pending';
    case 'approved':
      return 'approved';
    default:
      return 'pending';
  }
};

// Helper functions for billing response mapping
const mapBillingStatusToModel = (status: string): SaleStatus => {
  switch (status) {
    case 'مسلمة':
      return 'delivered';
    case 'مدفوعة':
      return 'delivered';
    case 'معلقة':
      return 'pending';
    case 'ملغية':
      return 'cancelled';
    default:
      return 'pending';
  }
};

const mapBillingPaymentMethodToModel = (paymentMethod: string): PaymentMethod => {
  switch (paymentMethod) {
    case 'نقداً':
      return 'cash';
    case 'آجل':
      return 'credit';
    case 'تحويل بنكي':
      return 'bank_transfer';
    case 'جزئي':
      return 'credit';
    default:
      return 'credit';
  }
};

const mapBillingPaymentStatusToModel = (paymentStatus: string): PaymentStatus => {
  switch (paymentStatus) {
    case 'مدفوع بالكامل':
      return 'paid';
    case 'مدفوع جزئياً':
      return 'pending';
    case 'غير مدفوعة':
      return 'pending';
    case 'قيد المراجعة':
      return 'pending';
    default:
      return 'pending';
  }
};

// Helper function to map sale item from API
const mapSaleItemAPIToModel = (item: any): SaleItem => {
  return {
    id: item.id.toString(),
    salesOrderId: item.id.toString(), // Use id as salesOrderId since sales_order_id doesn't exist
    productItemId: undefined, // product_item_id doesn't exist in new API
    productId: item.id.toString(), // Use id as productId since product_id doesn't exist
    productName: item.product_name,
    quantity: item.quantity,
    unitPrice: typeof item.unit_price === 'number' ? item.unit_price : parseNumber(item.unit_price),
    subtotal: typeof item.subtotal === 'number' ? item.subtotal : parseNumber(item.subtotal),
    unit: item.unit,
    costPrice: item.cost_price ? (typeof item.cost_price === 'number' ? item.cost_price : parseNumber(item.cost_price)) : undefined,
    price: typeof item.price === 'number' ? item.price : parseNumber(item.price),
    serialNumber: item.serial_number ?? null
  };
};

// Map array of API SaleDTOs to Sale models
export const mapAPISalesDTOToModel = (dtos: import('../api/SalesAPIDataSource').LegacySaleDTO[]): Sale[] => {
  return dtos.map(mapAPISaleDTOToModel);
};

// Map API SalesStatistics to Domain SalesStatistics
export const mapAPISalesStatisticsToModel = (dto: any): SalesStatistics => {
  return {
    total_invoices: dto.total_invoices || 0,
    paid_invoices: dto.paid_invoices || 0,
    partially_paid_invoices: dto.partially_paid_invoices || 0,
    unpaid_invoices: dto.unpaid_invoices || 0,
    total_amount: dto.total_amount || 0,
    total_paid: dto.total_paid || 0,
    total_remaining: dto.total_remaining || 0,
    total_outstanding: dto.total_outstanding || 0,
    invoice_count: dto.invoice_count || 0,
    top_customers: dto.top_customers || [],
    aging_report: dto.aging_report || {
      current: 0,
      days_1_30: 0,
      days_31_60: 0,
      days_61_90: 0,
      over_90: 0
    }
  };
};
